// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { api } from '../api/client';
import { CaseRoleProvider } from './CaseRoleContext';
import { JobsProvider } from './JobsContext';
import { LabelQueueProvider } from './LabelQueueContext';
import { AppShell } from './AppShell';
import { INDSTILLINGER_PATH, OVERBLIK_PATH, SEGMENTERING_PATH, SKABELONER_PATH } from './links';
import {
  MODES,
  QUERY_REDIRECTS,
  REDIRECTS,
  queryRedirectTarget,
  type QueryRedirect as QueryRedirectRule,
} from './navigation';
import { BygningShell } from '../routes/bygning/BygningShell';
import { ToolFrame } from '../routes/bygning/ToolFrame';
import { LabelsPage } from '../routes/DataPage';
import { FramesPage } from '../routes/FramesPage';
import { GeometryPage } from '../routes/GeometryPage';
import { IndstillingerPage } from '../routes/IndstillingerPage';
import { InstancesPage } from '../routes/InstancesPage';
import { PipelineLogPage } from '../routes/PipelineLogPage';
import { PipelinePage } from '../routes/PipelinePage';
import { SegmenteringPage } from '../routes/SegmenteringPage';
import { SkabelonerPage } from '../routes/SkabelonerPage';

/** A retired path that keeps part of its query (`/kortlaegning?type=3` → `/ressourcer?type=3`). */
function QueryRedirect({ rule }: { rule: QueryRedirectRule }) {
  const { search } = useLocation();
  return <Navigate to={queryRedirectTarget(rule, search)} replace />;
}

/**
 * Route table of one case.
 *
 * It runs under a router whose basename is the case prefix `/sager/:cid`
 * (`main.tsx`, `app/cases.ts`), so every path here is case-relative and
 * `/ressourcer` means `/sager/<cid>/ressourcer`. The case list (`/sager`) is
 * a separate app, `CasesApp`.
 *
 * Bygning først (PLAN.md): the five modes are child routes of ONE layout
 * route rendering `BygningShell`, so switching mode never remounts the shell
 * — the Ground's scene and the case store stay in memory (this replaces the
 * retired always-mounted viewport page). The expert pages are children of the
 * `ToolFrame` layout. Retired paths (`/kortlaegning`, `/indberetning`,
 * `/viewport`, `/sagsdata`, `/graph-view`, …) redirect (`navigation.ts`).
 *
 * Paths stay extensionless on purpose: `ruxd --local`'s SPA fallback only rewrites
 * extensionless requests to `index.html`, so that a missing `/assets/app.js`
 * still 404s instead of silently returning HTML. A route containing a dot would
 * break on reload.
 */
function RoutedContent() {
  return (
    <Routes>
      <Route element={<BygningShell />}>
        {MODES.flatMap((m) => [
          <Route key={m.id} path={m.path} element={null} />,
          // A mode's sub-tabs are nested routes of the same shell (Rediger › Scan & pose, …).
          ...(m.tabs ?? []).map((t) => <Route key={`${m.id}/${t.id}`} path={`${m.path}/${t.id}`} element={null} />),
        ])}
      </Route>

      <Route element={<ToolFrame />}>
        <Route path="/pipeline" element={<PipelinePage />} />
        <Route path="/pipeline/log" element={<PipelineLogPage />} />
        <Route path="/frames" element={<FramesPage />} />
        <Route path="/geometry" element={<GeometryPage />} />
        <Route path="/instances" element={<InstancesPage />} />
        <Route path="/labels" element={<LabelsPage />} />
        <Route path={SEGMENTERING_PATH} element={<SegmenteringPage />} />
        <Route path={SKABELONER_PATH} element={<SkabelonerPage />} />
        <Route path={INDSTILLINGER_PATH} element={<IndstillingerPage />} />
      </Route>

      {QUERY_REDIRECTS.map((r) => (
        <Route key={r.from} path={r.from} element={<QueryRedirect rule={r} />} />
      ))}
      {REDIRECTS.map((r) => (
        <Route key={r.from} path={r.from} element={<Navigate to={r.to} replace />} />
      ))}
      <Route path="*" element={<Navigate to={OVERBLIK_PATH} replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <CaseRoleProvider cid={api.caseId}>
      <JobsProvider>
        <LabelQueueProvider>
          <AppShell>
            <RoutedContent />
          </AppShell>
        </LabelQueueProvider>
      </JobsProvider>
    </CaseRoleProvider>
  );
}
